/**
 * Support chat. Own messages take the button colour, admin replies sit on surface — the same
 * asymmetry Telegram uses for outgoing/incoming, so the screen reads as a chat rather than a
 * form. Sends are optimistic with a pending state, and the thread polls while it is open.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { SupportMessage, SupportThreadView } from '@promptvault/shared';
import { fetchSupportThread, sendSupportMessage } from '../api/community';
import { Icon } from '../components/Icon';
import { Screen } from '../components/Screen';
import { useToast } from '../components/Toast';
import { ErrorState, SkeletonComments } from '../components/feedback';
import { clockTime, isSameDay } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { haptic, hapticNotify } from '../hooks/useTg';

const POLL_MS = 15_000;

export function Support() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t, lang } = useI18n();
  const [draft, setDraft] = useState('');
  const bottom = useRef<HTMLDivElement>(null);

  const thread = useQuery({
    queryKey: ['support'],
    queryFn: fetchSupportThread,
    refetchInterval: POLL_MS,
  });

  const sendMut = useMutation({
    mutationFn: (body: string) => sendSupportMessage(body),
    onMutate: (body) => {
      const previous = qc.getQueryData<SupportThreadView>(['support']);
      // Optimistic bubble with a temporary id; the refetch replaces it with the real row.
      const optimistic: SupportMessage = {
        id: `pending-${Date.now()}`,
        sender: 'user',
        body,
        createdAt: Date.now(),
      };
      if (previous) {
        qc.setQueryData<SupportThreadView>(['support'], {
          ...previous,
          messages: [...previous.messages, optimistic],
        });
      }
      setDraft('');
      return { previous };
    },
    onSuccess: () => {
      hapticNotify('success');
      void qc.invalidateQueries({ queryKey: ['support'] });
    },
    onError: (_e, body, ctx) => {
      if (ctx?.previous) qc.setQueryData(['support'], ctx.previous);
      setDraft(body);
      hapticNotify('error');
      toast(t.errorTitle, 'error');
    },
  });

  const messages = thread.data?.messages ?? [];

  // Stick to the newest message whenever the thread grows.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  const send = () => {
    const body = draft.trim();
    if (!body || sendMut.isPending) return;
    haptic('light');
    sendMut.mutate(body);
  };

  return (
    // The thread grows from the bottom: `justify-end` keeps a short conversation sitting on
    // the composer instead of stranding it halfway up an empty screen.
    <Screen chrome="page" className="min-h-full" contentClassName="flex flex-col justify-end">
      <div className="flex flex-col gap-2 px-3 pb-3 pt-3.5">
        {thread.isLoading ? (
          <SkeletonComments count={3} />
        ) : thread.isError ? (
          <ErrorState
            message={(thread.error as Error)?.message}
            onRetry={() => void thread.refetch()}
            compact
          />
        ) : messages.length === 0 ? (
          <p className="px-6 py-10 text-center text-subhead leading-relaxed text-hint">
            {t.supportEmpty}
          </p>
        ) : (
          messages.map((m, i) => {
            const prev = messages[i - 1];
            const newDay = !prev || !isSameDay(prev.createdAt, m.createdAt);
            const mine = m.sender === 'user';
            const pending = m.id.startsWith('pending-');
            return (
              <div key={m.id} className="flex flex-col gap-2">
                {newDay && (
                  <span className="self-center rounded-full bg-fill-strong px-3 py-1 text-caption text-hint">
                    {isSameDay(Date.now(), m.createdAt)
                      ? t.supportToday
                      : new Date(m.createdAt).toLocaleDateString(lang === 'fa' ? 'fa-IR' : 'en-GB')}
                  </span>
                )}
                <div className={`flex px-1 ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[78%] px-3 pb-1.5 pt-2 ${
                      mine
                        ? 'rounded-[14px] rounded-ee-[4px] bg-button text-button-text'
                        : 'rounded-[14px] rounded-es-[4px] bg-surface text-text'
                    } ${pending ? 'opacity-60' : ''}`}
                  >
                    {!mine && (
                      <p className="pb-0.5 text-footnote font-semibold text-accent">
                        {t.supportAgent}
                      </p>
                    )}
                    {/* plaintext so an English reply inside an RTL page keeps its own
                        direction — otherwise trailing punctuation jumps to the far end. */}
                    <p
                      dir="auto"
                      className="text-balance whitespace-pre-wrap text-start text-callout leading-relaxed"
                    >
                      {m.body}
                    </p>
                    <p
                      className={`tnum pt-0.5 text-end text-caption2 ${
                        mine ? 'text-button-text/70' : 'text-subtitle'
                      }`}
                    >
                      {clockTime(m.createdAt, lang)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottom} aria-hidden="true" />
      </div>

      {/* Composer pinned below the thread, above the home indicator. */}
      <div
        className="hairline-t sticky bottom-0 flex items-end gap-2 bg-surface px-3 pt-2"
        style={{ paddingBottom: 'calc(var(--pv-space-2) + var(--pv-safe-bottom))' }}
      >
        <div className="flex min-h-[40px] min-w-0 flex-1 items-center rounded-[18px] bg-fill px-3.5">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') send();
            }}
            placeholder={t.supportPh}
            maxLength={4000}
            enterKeyHint="send"
            className="h-10 min-w-0 flex-1 border-0 bg-transparent text-body text-text outline-none placeholder:text-hint"
          />
        </div>
        <button
          type="button"
          aria-label={t.supportPh}
          disabled={draft.trim().length === 0 || sendMut.isPending}
          onClick={send}
          className={`flex h-10 w-10 flex-none items-center justify-center rounded-full transition-[background,color] duration-200 ease-pv ${
            draft.trim() ? 'bg-button text-button-text' : 'bg-fill text-chevron'
          }`}
        >
          <Icon name="send" size={19} />
        </button>
      </div>
    </Screen>
  );
}
